import React, { useState } from 'react';
import { Input, Modal, Spin, Radio, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useL202Form } from './hooks/useL202Form';
import LookupField from '../../components/LookupField';
import SharedActionBar from '../../components/SharedActionBar/SharedActionBar';

const { TextArea } = Input;

const ATTACH_TYPE_OPTIONS = [
  { value: 1, label: '1-無' },
  { value: 2, label: '2-DOC' },
  { value: 3, label: '3-XLS' },
  { value: 4, label: '4-PPT' },
  { value: 5, label: '5-HTM' },
  { value: 6, label: '6-PDF' },
];

const L202Form: React.FC = () => {
  const f = useL202Form();
  const navigate = useNavigate();
  const [searchDoc, setSearchDoc] = useState('');

  const handleDelete = () => {
    if (!f.current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${f.current.fl120_01}」第 ${f.current.fl120_02} 章節「${f.current.fl120_11}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: f.doDelete,
    });
  };

  const handleSearch = () => {
    const doc = searchDoc.trim();
    if (doc) f.applyFilter(doc);
  };

  const handlePrint = () => {
    message.info('列印功能尚未實作');
  };

  return (
    <Spin spinning={f.loading}>
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '16px 24px' }}>
        {/* Title */}
        <h2 style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12 }}>
          章節內容維護 (pwl202f1)
          <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 'normal', color: '#888' }}>
            [{f.mode === 'add' ? '新增' : f.mode === 'edit' ? '修改' : '瀏覽'}]
            {f.records.length > 0 ? ` ${f.currentIdx + 1} / ${f.records.length}` : ' 無資料'}
          </span>
        </h2>

        {/* Document Filter Bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: '#666' }}>文件編號篩選：</span>
          <Input
            placeholder="輸入文件編號（FL020）"
            value={searchDoc}
            onChange={e => setSearchDoc(e.target.value)}
            onPressEnter={handleSearch}
            disabled={f.isEditing}
            style={{ width: 180 }}
            maxLength={10}
          />
          <button className="sab-btn" onClick={handleSearch} disabled={f.isEditing}>查詢</button>
          <button
            className="sab-btn"
            onClick={() => { setSearchDoc(''); f.applyFilter(''); }}
            disabled={f.isEditing}
          >
            全部
          </button>
        </div>

        {/* Row 1: 章節編號 | 名稱 | 建檔日期 — matches FoxPro top ~61 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <label style={{ width: 70, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>章節編號</label>
          <Input
            value={f.docCode}
            onChange={e => f.setDocCode(e.target.value)}
            maxLength={10}
            disabled={f.mode !== 'add'}
            style={{ width: 100 }}
          />
          <label style={{ width: 36, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>名稱</label>
          <Input
            value={f.name}
            onChange={e => f.setName(e.target.value)}
            maxLength={50}
            disabled={!f.isEditing}
            style={{ width: 255 }}
          />
          <label style={{ width: 60, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>建檔日期</label>
          <Input
            value={f.current?.fl120_13 ? String(f.current.fl120_13).substring(0, 10) : ''}
            readOnly
            disabled
            style={{ width: 100 }}
          />
        </div>

        {/* Row 2: 隸屬文件 | 類別 | 部門 — matches FoxPro top ~89 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <label style={{ width: 70, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>隸屬文件</label>
          <LookupField
            lookupType="documentSetup"
            value={f.parentDoc || ''}
            onChange={(code) => f.setParentDoc(code)}
            disabled={!f.isEditing}
            style={{ width: 100 }}
            title="搜尋文件"
          />
          <label style={{ width: 36, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>類別</label>
          <Input
            value={f.refData?.fl020_16 || ''}
            readOnly
            disabled
            style={{ width: 100 }}
          />
          <label style={{ width: 36, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>部門</label>
          <Input
            value={f.refData?.fl020_10 || ''}
            readOnly
            disabled
            style={{ width: 50 }}
          />
        </div>

        {/* Row 3: 附件類型 | 附件查詢 | 行數 — matches FoxPro top ~112-120 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <label style={{ width: 70, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>附件類型</label>
          <Radio.Group
            value={f.attachType}
            onChange={e => f.setAttachType(e.target.value)}
            disabled={!f.isEditing}
            size="small"
          >
            {ATTACH_TYPE_OPTIONS.map(opt => (
              <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
            ))}
          </Radio.Group>
          <button
            className="sab-btn"
            disabled={!f.current}
            onClick={() => message.info('附件查詢功能尚未實作')}
            style={{ marginLeft: 8 }}
          >
            附件查詢
          </button>
          <label style={{ width: 36, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 }}>行數</label>
          <Input
            value={f.memline}
            readOnly
            disabled
            style={{ width: 50 }}
          />
        </div>

        {/* Row 4: 章節內容 — matches FoxPro edtFl120_21 (639x354) */}
        <TextArea
          value={f.content}
          onChange={e => f.setContent(e.target.value)}
          disabled={!f.isEditing}
          rows={14}
          style={{ width: '100%', marginBottom: 12 }}
          placeholder="章節內容文字"
        />

        {/* SharedActionBar — replaces inline buttons, matches FoxPro Pwtcmd1 */}
        <SharedActionBar
          mode={f.mode}
          isEditing={f.isEditing}
          hasRecord={!!f.current}
          currentIdx={f.currentIdx}
          recordCount={f.records.length}
          onNavFirst={f.navFirst}
          onNavPrev={f.navPrev}
          onNavNext={f.navNext}
          onNavLast={f.navLast}
          onAdd={f.startAdd}
          onModify={f.startEdit}
          onDelete={handleDelete}
          onSave={f.save}
          onCancel={f.cancel}
          onPrint={handlePrint}
          onExit={() => navigate(-1)}
        />
      </div>
    </Spin>
  );
};

export default L202Form;
